import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatUSD } from "@/components/KPICard";
import { Calculator, TrendingUp, Target } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

export default function Simulator() {
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({
    property_id: "",
    years: 10,
    rent_growth_pct: 8,
    expense_growth_pct: 6,
    appreciation_pct: 5,
    base_income_usd: "",
    base_expense_usd: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProperties(r.data);
      if (r.data.length) setForm((f) => ({ ...f, property_id: r.data[0].id }));
    });
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const payload = {
        property_id: form.property_id,
        years: parseInt(form.years) || 10,
        rent_growth_pct: parseFloat(form.rent_growth_pct) || 0,
        expense_growth_pct: parseFloat(form.expense_growth_pct) || 0,
        appreciation_pct: parseFloat(form.appreciation_pct) || 0,
      };
      if (form.base_income_usd !== "") payload.base_income_usd = parseFloat(form.base_income_usd);
      if (form.base_expense_usd !== "") payload.base_expense_usd = parseFloat(form.base_expense_usd);
      const r = await api.post("/simulator/property", payload);
      setResult(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (form.property_id) run(); /* eslint-disable-next-line */ }, [form.property_id]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Simulador financiero</h1>
        <div className="text-sm text-muted-foreground mt-1">
          Proyectá ingresos, gastos, valor y ROI de una propiedad a N años. Base: últimos 12 meses en USD.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 sm:gap-6">
        {/* Input panel */}
        <div className="rounded-xl border border-border/60 bg-card p-5 space-y-4 h-fit">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Parámetros</div>
          <div>
            <Label>Propiedad</Label>
            <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
              <SelectTrigger data-testid="simulator-property"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Años</Label>
              <Input type="number" min="1" max="30" value={form.years} onChange={(e) => setForm({ ...form, years: e.target.value })} />
            </div>
            <div>
              <Label>Aumento renta % / año</Label>
              <Input type="number" step="0.1" value={form.rent_growth_pct} onChange={(e) => setForm({ ...form, rent_growth_pct: e.target.value })} />
            </div>
            <div>
              <Label>Aumento gastos % / año</Label>
              <Input type="number" step="0.1" value={form.expense_growth_pct} onChange={(e) => setForm({ ...form, expense_growth_pct: e.target.value })} />
            </div>
            <div>
              <Label>Apreciación % / año</Label>
              <Input type="number" step="0.1" value={form.appreciation_pct} onChange={(e) => setForm({ ...form, appreciation_pct: e.target.value })} />
            </div>
          </div>
          <div className="pt-3 border-t border-border/60 space-y-3">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Base (opcional, override 12m)</div>
            <div>
              <Label>Ingreso anual USD</Label>
              <Input type="number" step="0.01" placeholder="auto" value={form.base_income_usd} onChange={(e) => setForm({ ...form, base_income_usd: e.target.value })} />
            </div>
            <div>
              <Label>Gasto anual USD</Label>
              <Input type="number" step="0.01" placeholder="auto" value={form.base_expense_usd} onChange={(e) => setForm({ ...form, base_expense_usd: e.target.value })} />
            </div>
          </div>
          <Button className="w-full rounded-full" onClick={run} disabled={loading || !form.property_id} data-testid="simulator-run">
            <Calculator className="w-4 h-4 mr-1" /> {loading ? "Calculando…" : "Simular"}
          </Button>
        </div>

        {/* Results */}
        {result ? (
          <div className="space-y-5">
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: "Valor inicial USD", value: formatUSD(result.property.value_usd), tone: "text-foreground" },
                { label: `Valor a ${result.assumptions.years} años`, value: formatUSD(result.summary.final_value_usd), tone: "text-emerald-500", Icon: TrendingUp },
                { label: "NOI acumulado", value: formatUSD(result.summary.total_noi_usd), tone: "text-sky-500" },
                { label: "Retorno total", value: `${result.summary.total_return_pct.toFixed(1)}%`, tone: result.summary.total_return_pct >= 0 ? "text-emerald-500" : "text-rose-500" },
              ].map((k, i) => {
                const Icon = k.Icon;
                return (
                  <div key={i} className="rounded-xl border border-border/60 bg-card p-4">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
                    <div className={`font-heading text-xl sm:text-2xl font-semibold num mt-1 ${k.tone} inline-flex items-center gap-1`}>
                      {Icon && <Icon className="w-4 h-4" />} {k.value}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Baseline info */}
            <div className="rounded-xl border border-border/60 bg-card p-4 flex flex-wrap gap-4 text-sm">
              <div><span className="text-muted-foreground">Ingreso 12m:</span> <strong className="num">{formatUSD(result.baseline.trailing_12m_income_usd)}</strong></div>
              <div><span className="text-muted-foreground">Gasto 12m:</span> <strong className="num">{formatUSD(result.baseline.trailing_12m_expense_usd)}</strong></div>
              <div><span className="text-muted-foreground">NOI 12m:</span> <strong className="num">{formatUSD(result.baseline.trailing_12m_noi_usd)}</strong></div>
              <div><span className="text-muted-foreground">Cap rate actual:</span> <strong className="num">{result.baseline.current_cap_rate_pct.toFixed(2)}%</strong></div>
              {result.summary.payback_year && (
                <div className="inline-flex items-center gap-1 text-emerald-500"><Target className="w-4 h-4" /> <strong>Payback: {result.summary.payback_year}</strong></div>
              )}
            </div>

            {/* Chart */}
            <div className="rounded-xl border border-border/60 bg-card p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-3">Proyección año por año</div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={result.projection}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                    <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis yAxisId="left" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="income" name="Ingreso" fill="#22C55E" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="expense" name="Gasto" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="left" type="monotone" dataKey="noi" name="NOI" stroke="hsl(var(--foreground))" strokeWidth={2.5} dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="property_value" name="Valor propiedad" stroke="#8B5CF6" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Table */}
            <div className="rounded-xl border border-border/60 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60">
                  <tr className="text-left">
                    <th className="px-3 py-2.5 font-medium">Año</th>
                    <th className="px-3 py-2.5 font-medium text-right">Ingreso</th>
                    <th className="px-3 py-2.5 font-medium text-right">Gasto</th>
                    <th className="px-3 py-2.5 font-medium text-right">NOI</th>
                    <th className="px-3 py-2.5 font-medium text-right">NOI acumulado</th>
                    <th className="px-3 py-2.5 font-medium text-right">Valor propiedad</th>
                    <th className="px-3 py-2.5 font-medium text-right">Cash-on-cash</th>
                    <th className="px-3 py-2.5 font-medium text-right">Retorno total</th>
                  </tr>
                </thead>
                <tbody>
                  {result.projection.map((r) => (
                    <tr key={r.year} className="border-t border-border/50 hover:bg-secondary/40">
                      <td className="px-3 py-2 num">{r.year}</td>
                      <td className="px-3 py-2 text-right num text-emerald-500">{formatUSD(r.income)}</td>
                      <td className="px-3 py-2 text-right num text-rose-500">{formatUSD(r.expense)}</td>
                      <td className="px-3 py-2 text-right num font-semibold">{formatUSD(r.noi)}</td>
                      <td className="px-3 py-2 text-right num">{formatUSD(r.cumulative_noi)}</td>
                      <td className="px-3 py-2 text-right num text-violet-500">{formatUSD(r.property_value)}</td>
                      <td className="px-3 py-2 text-right num">{r.cash_on_cash_pct.toFixed(2)}%</td>
                      <td className={`px-3 py-2 text-right num font-semibold ${r.total_return_pct >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{r.total_return_pct.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-border/60 bg-card p-10 text-center text-muted-foreground">
            Elegí una propiedad y ajustá los parámetros. La simulación se corre automáticamente.
          </div>
        )}
      </div>
    </div>
  );
}
